// Pantalla intermedia de los atajos de "Ediciones rápidas" —
// ADR-2026-08-20-los-atajos-tienen-pantalla-propia-para-elegir-imagen.
//
// Cuatro de los siete atajos (Editar, Recortar, Mejorar, Descripción) navegaban al
// historial y ahí se terminaba: el usuario tocaba "Mejorar" y aterrizaba en una grilla
// de creaciones sin ninguna señal de qué había pedido.
//
// El layout es EL MISMO que el del modo Carrusel, a pedido explícito
// (ADR-2026-08-20-la-pantalla-de-atajos-copia-el-layout-del-carrusel): panel `fixed` que
// tapa el contenido, hero a la izquierda + card blanca de opciones a la derecha en
// desktop, y en mobile carátula full-bleed con panel blanco redondeado abajo y la grilla
// dentro de un bottom sheet. No es un parecido: se replican las mismas clases, los mismos
// breakpoints y el mismo `left: sidebarWidth`.
//
// SÓLO "elegir una ya generada": subir una imagen propia queda afuera POR AHORA (decisión
// del usuario). Los tres efectos que corren en el backend piden el `id` de una fila de
// `imagenes_generadas` —`/v1/images/upscale` y `/v1/images/instagram-description` con
// `image_id: uuid`, `/v1/images/edit` con `imagen_id: uuid`— y una foto subida no tiene
// fila. Por eso acá no está el botón azul "Subir imagen" que el carrusel sí tiene: sería
// prometer algo que corta al final del flujo.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  Crop,
  Images,
  Instagram,
  Maximize,
  Pencil,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useSidebar } from '../../../context/SidebarContext';
import OptionSheet from '../../../components/opciones/OptionSheet';
import {
  GeneradasPickGrid,
  type GeneradaPick,
} from './shared/GeneradasPickGrid';
import {
  GeneratedImagesHistory,
  type AccionVisor,
} from '../../GeneratedImagesHistory';

/** Los atajos que pasan por acá. El slug es el `:accion` de la URL. */
export type SlugAtajo = 'editar' | 'recortar' | 'mejorar' | 'descripcion';

interface ConfigAtajo {
  /** Título del bloque de la card, en imperativo: es la consigna del paso. */
  titulo: string;
  /** Qué va a pasar con la imagen elegida. */
  bajada: string;
  /** Copy del hero (desktop), abajo de la carátula. */
  heroDesc: string;
  Icono: LucideIcon;
  /**
   * Carátula del atajo. `null` = todavía no hay arte propio y se dibuja el ícono grande
   * en su lugar. Sólo Editar tiene una real (`editar_prompt.png`, el antes/después con la
   * flecha); las otras tres están PENDIENTES de arte, igual que en su momento lo
   * estuvieron las de Carrusel y Shorts Studio.
   */
  hero: string | null;
  /** Texto del vacío de la grilla, nombrando lo que este atajo hace. */
  emptyHint: string;
  /**
   * Cómo se aplica el efecto:
   *  - `visor`: se monta el visor del historial (`soloVisor`) con la acción disparada.
   *    Son los que NO tienen pantalla propia y viven adentro de ese componente.
   *  - `ruta`: el efecto ya tiene su pantalla; se navega con el id de la imagen.
   */
  destino:
    | { tipo: 'visor'; accion: AccionVisor }
    | { tipo: 'ruta'; armar: (imageId: string, companyId: string) => string };
}

const ATAJOS: Record<SlugAtajo, ConfigAtajo> = {
  editar: {
    titulo: 'Elegí la imagen a editar',
    bajada: 'Después le pedís el cambio con un prompt y Atlas lo aplica.',
    heroDesc:
      'Pedile el cambio en palabras —otro fondo, otro color, sacar algo— y Atlas lo resuelve sobre tu foto.',
    Icono: Pencil,
    hero: '/images/editar_prompt.png',
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para editarla.',
    // Editar SÍ tiene pantalla propia y sabe recibir la imagen por query params
    // (`EditarImagen` lee `?id=` y `?companyId=`), así que no hace falta el visor.
    destino: {
      tipo: 'ruta',
      armar: (imageId, companyId) =>
        `/redes-sociales/editar-imagen?id=${encodeURIComponent(imageId)}&companyId=${encodeURIComponent(companyId)}`,
    },
  },
  recortar: {
    titulo: 'Elegí la imagen a recortar',
    bajada: 'Después ajustás el encuadre y la descargás.',
    heroDesc:
      'Ajustá el encuadre a la proporción que necesites para cada red, sin salir de Atlas.',
    Icono: Crop,
    hero: null,
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para recortarla.',
    destino: { tipo: 'visor', accion: 'recortar' },
  },
  mejorar: {
    titulo: 'Elegí la imagen a mejorar',
    bajada: 'Atlas le sube la resolución con IA, sin perder detalle.',
    heroDesc:
      'Subí la resolución de una creación con IA: sirve para imprimir o para publicarla en grande.',
    Icono: Maximize,
    hero: null,
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para mejorarla.',
    destino: { tipo: 'visor', accion: 'mejorar' },
  },
  descripcion: {
    titulo: 'Elegí la imagen a publicar',
    bajada: 'Atlas te escribe el texto del post a partir de la foto.',
    heroDesc:
      'Atlas mira la imagen y escribe el texto para publicarla, con hashtags incluidos.',
    Icono: Instagram,
    hero: null,
    emptyHint:
      'Todavía no generaste ninguna imagen. Generá una y volvé para escribir su descripción.',
    destino: { tipo: 'visor', accion: 'descripcion' },
  },
};

/** ¿Es un slug de atajo conocido? */
export function esSlugAtajo(v: string | undefined): v is SlugAtajo {
  return !!v && v in ATAJOS;
}

export default function AtajoImagenMode() {
  const navigate = useNavigate();
  const { accion } = useParams<{ accion: string }>();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();

  const config = esSlugAtajo(accion) ? ATAJOS[accion] : null;

  const [companyId, setCompanyId] = useState<string | null>(null);
  /** La imagen elegida. `null` = todavía estamos en el paso de elegir. */
  const [elegida, setElegida] = useState<GeneradaPick | null>(null);
  /** Mobile: la grilla vive en un sheet, así el panel de abajo no se llena. */
  const [sheetGeneradas, setSheetGeneradas] = useState(false);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (!cancelado) {
        setCompanyId((p as { id_company?: string | null } | null)?.id_company ?? null);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // Un slug que no existe no muestra una pantalla rota: vuelve a la home.
  useEffect(() => {
    if (!config) navigate('/interfaz-v2', { replace: true });
  }, [config, navigate]);

  // Back flotante del shell en mobile, igual que Carrusel y Animar: además de dar el
  // botón, esconde la barra inferior (se apaga con `mobileBackOverride !== null`).
  useEffect(() => {
    setMobileBackOverride(() => () => navigate('/interfaz-v2'));
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config) return null;

  const alElegir = (pick: GeneradaPick) => {
    setSheetGeneradas(false);
    if (config.destino.tipo === 'ruta') {
      if (!companyId) return;
      navigate(config.destino.armar(pick.id, companyId));
      return;
    }
    setElegida(pick);
  };

  // Paso 2 (sólo los `visor`): el visor del historial, prestado, con la acción ya
  // disparada. Al cerrarlo se vuelve al paso de elegir y no a la home: quien entró por
  // "Mejorar" probablemente quiera mejorar otra.
  if (elegida && config.destino.tipo === 'visor') {
    return (
      <GeneratedImagesHistory
        soloVisor={{
          imageId: elegida.id,
          accionInicial: config.destino.accion,
          onCerrar: () => setElegida(null),
        }}
      />
    );
  }

  const { Icono } = config;

  /** La carátula del atajo, o el ícono grande cuando todavía no hay arte propio. */
  const heroImg = (className: string) =>
    config.hero ? (
      <img src={config.hero} alt="" className={className} />
    ) : (
      // Mismo lenguaje que el chip de la card, en grande: cuadrado redondeado con el
      // tinte de marca. No es un placeholder gris — se lee como una pieza más del
      // sistema mientras no exista la carátula real.
      <div
        className={`${className} grid aspect-square w-full place-items-center rounded-2xl bg-[#1644EB]/[0.07]`}
      >
        <Icono className="h-16 w-16 text-brand-primary" strokeWidth={1.4} />
      </div>
    );

  /** Encabezado + CTA del aporte de imagen. Mismo bloque que el del carrusel, sin el
   *  botón azul de "Subir imagen" (ver el comentario de arriba del archivo). */
  const bloqueElegirImagen = (variant: 'mobile' | 'desktop') => (
    <>
      <div className="flex items-start gap-3.5 mb-4">
        <div className="w-11 h-11 rounded-xl bg-[#1644EB]/10 flex items-center justify-center flex-shrink-0">
          <Icono className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-brand-black leading-tight">
            {config.titulo}
          </p>
          <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
            {config.bajada}
          </p>
        </div>
      </div>

      {variant === 'mobile' ? (
        <button
          type="button"
          onClick={() => setSheetGeneradas(true)}
          className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors"
        >
          <Images className="w-5 h-5" /> Elegir de mis generadas
        </button>
      ) : (
        <div className="flex items-center gap-3 mt-1 mb-1">
          <div className="flex-1 h-px bg-black/[0.06]" />
          <span className="text-[12px] text-[#3C3C43]/45 shrink-0">
            elegí una que ya generaste
          </span>
          <div className="flex-1 h-px bg-black/[0.06]" />
        </div>
      )}
    </>
  );

  return (
    <>
      {/* ── MOBILE: carátula full-bleed + panel blanco redondeado abajo ────── */}
      <div className="md:hidden fixed inset-0 z-30 bg-surface-mobile flex flex-col">
        <div className="relative flex-1 min-h-0 bg-neutral-100">
          {config.hero ? (
            <img
              src={config.hero}
              alt=""
              className="absolute inset-0 w-full h-full object-contain p-6"
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <Icono className="h-20 w-20 text-brand-primary/70" strokeWidth={1.3} />
            </div>
          )}
        </div>
        <div
          className="relative z-10 bg-white shrink-0 -mt-6 rounded-t-3xl flex flex-col overflow-hidden"
          style={{ maxHeight: '55dvh' }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-7 pb-6">
            {bloqueElegirImagen('mobile')}
          </div>
        </div>
      </div>

      {/* ── DESKTOP: panel a la derecha de la sidebar, dos columnas ────────── */}
      <div
        className="hidden md:flex fixed inset-y-0 right-0 z-30 bg-surface-content rounded-tl-[26px] rounded-bl-[26px] flex-col"
        style={{ left: sidebarWidth }}
      >
        <header className="shrink-0 px-6 pt-5 pb-3">
          <button
            type="button"
            onClick={() => navigate('/interfaz-v2')}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
        </header>

        <div className="px-8 pb-6 flex flex-row gap-6 justify-center flex-1 min-h-0 items-stretch">
          {/* Columna izquierda: carátula del atajo + copy. */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3 min-w-0">
            {heroImg('max-w-[420px] max-h-[60%] w-auto h-auto object-contain rounded-2xl')}
            <p className="text-[12.5px] text-[#3C3C43]/60 leading-relaxed text-center text-balance max-w-[360px]">
              {config.heroDesc}
            </p>
          </div>

          {/* Columna derecha: card blanca con la grilla inline. */}
          <aside className="shrink-0 flex-1 max-w-[560px] flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-6">
              {bloqueElegirImagen('desktop')}
              <div className="mt-3">
                <GeneradasPickGrid
                  companyId={companyId}
                  selectedId={null}
                  onSelect={alElegir}
                  emptyHint={config.emptyHint}
                />
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile: la grilla en un bottom sheet, igual que el carrusel. */}
      <OptionSheet
        open={sheetGeneradas}
        onClose={() => setSheetGeneradas(false)}
        title="Mis imágenes generadas"
        hideAccept
      >
        <GeneradasPickGrid
          companyId={companyId}
          selectedId={null}
          onSelect={alElegir}
          emptyHint={config.emptyHint}
        />
      </OptionSheet>
    </>
  );
}
